<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import AddSubjectModal from '@/Components/Modals/AddSubjectModal.vue'

/**
 * What a tutor teaches, asked once, subject by subject.
 *
 * This replaces three separate sections — subjects, course levels, exam boards —
 * that each collected a flat list. Answered apart they described a cross-product
 * nobody claimed: a tutor teaching Spanish at A Level and History at GCSE was
 * recorded as teaching both subjects at both levels, which is what the tutor
 * listing then filtered on and what a tutor card had to show.
 *
 * A row is one subject with the levels it is taught at and the exam boards
 * followed, which is the same shape as the A-Level qualifications section
 * further down the form.
 */
const props = defineProps({
  subjects: {
    type: Array,
    required: true
  },
  levels: {
    type: Array,
    required: true
  },
  examboards: {
    type: Array,
    required: true
  },
  modelValue: {
    type: Array,
    default: () => []
  },
  errors: {
    type: Object,
    default: () => ({})
  },
  title: {
    type: String,
    default: 'Subjects & Levels'
  },
  description: {
    type: String,
    default: 'Add each subject you teach, then tick the levels and exam boards you cover for it'
  },
  showShadow: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:modelValue', 'subject-added'])

const localErrors = ref({})
const showAddSubjectModal = ref(false)
const localSubjects = ref([...props.subjects])

const displayErrors = computed(() => ({
  ...props.errors,
  ...localErrors.value
}))

watch(() => props.subjects, (subjects) => {
  localSubjects.value = [...subjects]
})

const emptyRow = () => ({ subject_id: null, level_ids: [], examboard_ids: [] })

const rows = computed(() => props.modelValue ?? [])

const canRemoveRow = computed(() => rows.value.length > 1)

const subjectById = (id) => localSubjects.value.find((subject) => subject.id === id)

/**
 * A subject already claimed in another row is dropped from this row's options,
 * so the same subject cannot be answered twice with different levels.
 */
const availableSubjects = (index) => {
  const takenElsewhere = rows.value
    .filter((_, rowIndex) => rowIndex !== index)
    .map((row) => row.subject_id)
    .filter(Boolean)

  return localSubjects.value.filter((subject) => !takenElsewhere.includes(subject.id))
}

const updateRows = (nextRows) => {
  emit('update:modelValue', nextRows)
}

const updateRow = (index, changes) => {
  const nextRows = rows.value.map((row, rowIndex) => (
    rowIndex === index ? { ...row, ...changes } : row
  ))

  updateRows(nextRows)
}

const clearError = (key) => {
  if (localErrors.value[key]) {
    const next = { ...localErrors.value }
    delete next[key]
    localErrors.value = next
  }
}

const addRow = () => {
  updateRows([...rows.value, emptyRow()])
  clearError('teaching')
}

const removeRow = (index) => {
  updateRows(rows.value.filter((_, rowIndex) => rowIndex !== index))
  localErrors.value = {}
}

const setSubject = (index, value) => {
  updateRow(index, { subject_id: value ? Number(value) : null })
  clearError(`teaching.${index}.subject_id`)
  clearError('teaching')
}

const toggleLevel = (index, levelId) => {
  const selected = [...(rows.value[index]?.level_ids ?? [])]
  const position = selected.indexOf(levelId)

  if (position === -1) {
    selected.push(levelId)
  } else {
    selected.splice(position, 1)
  }

  updateRow(index, { level_ids: selected })
  clearError(`teaching.${index}.level_ids`)
}

const toggleExamboard = (index, examboardId) => {
  const selected = [...(rows.value[index]?.examboard_ids ?? [])]
  const position = selected.indexOf(examboardId)

  if (position === -1) {
    selected.push(examboardId)
  } else {
    selected.splice(position, 1)
  }

  updateRow(index, { examboard_ids: selected })
  clearError(`teaching.${index}.examboard_ids`)
}

const isLevelSelected = (index, levelId) => (rows.value[index]?.level_ids ?? []).includes(levelId)

const isExamboardSelected = (index, examboardId) => (rows.value[index]?.examboard_ids ?? []).includes(examboardId)

const handleSubjectAdded = (newSubject) => {
  localSubjects.value = [...localSubjects.value, newSubject]

  const firstEmpty = rows.value.findIndex((row) => !row.subject_id)

  if (firstEmpty === -1) {
    updateRows([...rows.value, { ...emptyRow(), subject_id: newSubject.id }])
  } else {
    updateRow(firstEmpty, { subject_id: newSubject.id })
  }

  emit('subject-added', newSubject)
  showAddSubjectModal.value = false
}

const initialiseRows = () => {
  if (rows.value.length === 0) {
    updateRows([emptyRow()])
  }
}

onMounted(initialiseRows)

watch(() => props.modelValue, (value) => {
  if (!value || value.length === 0) {
    initialiseRows()
  }
})

const validate = () => {
  const errors = {}
  const answered = rows.value.filter((row) => row.subject_id)

  if (answered.length === 0) {
    errors.teaching = 'Please add at least one subject you teach'
  }

  rows.value.forEach((row, index) => {
    if (!row.subject_id) {
      if (rows.value.length > 1 || answered.length === 0) {
        errors[`teaching.${index}.subject_id`] = 'Please choose a subject'
      }

      return
    }

    if ((row.level_ids ?? []).length === 0) {
      errors[`teaching.${index}.level_ids`] = 'Please select at least one level'
    }

    if ((row.examboard_ids ?? []).length === 0) {
      errors[`teaching.${index}.examboard_ids`] = 'Please select at least one exam board'
    }
  })

  localErrors.value = errors

  return Object.keys(errors).length === 0
}

defineExpose({ validate })
</script>

<template>
  <div :class="['bg-white rounded-2xl p-4 md:p-10', showShadow ? 'shadow-lg' : '']">
    <div class="mb-4 md:mb-8">
      <h2 class="text-lg md:text-2xl font-bold text-gray-900 mb-2">
        {{ title }} <span class="text-red-500">*</span>
      </h2>
      <p class="text-gray-600 text-sm">{{ description }}</p>

      <!-- Section Error Message -->
      <div v-if="displayErrors.teaching" class="mt-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
        <div class="flex items-start gap-3">
          <span class="material-symbols-outlined text-red-600 text-xl mt-0.5">error</span>
          <div>
            <p class="text-red-600 font-semibold text-base">{{ displayErrors.teaching }}</p>
            <p class="text-red-700 text-sm mt-1">Add a subject, then tick the levels and exam boards you teach it at.</p>
          </div>
        </div>
      </div>
    </div>

    <div class="space-y-4 md:space-y-6">
      <div
        v-for="(row, index) in rows"
        :key="index"
        class="teaching-entry bg-gray-50 rounded-xl p-4 md:p-6 border-2 border-gray-200"
      >
        <!-- Subject -->
        <div class="flex items-end gap-3 mb-5">
          <div class="flex-1">
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              Subject <span class="text-red-500">*</span>
            </label>
            <select
              :value="row.subject_id ?? ''"
              @change="setSubject(index, $event.target.value)"
              class="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 appearance-none bg-white"
            >
              <option value="">Select subject</option>
              <option
                v-for="subject in availableSubjects(index)"
                :key="subject.id"
                :value="subject.id"
              >
                {{ subject.name }}
              </option>
            </select>
            <p v-if="displayErrors[`teaching.${index}.subject_id`]" class="text-red-500 text-sm mt-1">
              {{ displayErrors[`teaching.${index}.subject_id`] }}
            </p>
          </div>
          <button
            v-if="canRemoveRow"
            type="button"
            :aria-label="`Remove ${subjectById(row.subject_id)?.name || 'this subject'}`"
            @click="removeRow(index)"
            class="px-4 py-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all font-medium flex items-center justify-center"
          >
            <span class="material-symbols-outlined">delete</span>
          </button>
        </div>

        <!-- Levels -->
        <div class="mb-5">
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            Levels <span class="text-red-500">*</span>
            <span class="font-normal text-gray-500">— tick every level you teach this subject at</span>
          </label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="level in levels"
              :key="level.id"
              type="button"
              :aria-pressed="isLevelSelected(index, level.id)"
              @click="toggleLevel(index, level.id)"
              class="px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-all flex items-center gap-2"
              :class="isLevelSelected(index, level.id)
                ? 'border-brand-blue bg-blue-50 text-gray-900'
                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'"
            >
              <span
                class="material-symbols-outlined text-base"
                :class="isLevelSelected(index, level.id) ? 'text-brand-blue' : 'text-gray-300'"
              >
                {{ isLevelSelected(index, level.id) ? 'check_box' : 'check_box_outline_blank' }}
              </span>
              {{ level.name }}
            </button>
          </div>
          <p v-if="displayErrors[`teaching.${index}.level_ids`]" class="text-red-500 text-sm mt-2">
            {{ displayErrors[`teaching.${index}.level_ids`] }}
          </p>
        </div>

        <!-- Exam Boards -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            Exam boards <span class="text-red-500">*</span>
            <span class="font-normal text-gray-500">— tick every board you follow for it</span>
          </label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="examboard in examboards"
              :key="examboard.id"
              type="button"
              :aria-pressed="isExamboardSelected(index, examboard.id)"
              @click="toggleExamboard(index, examboard.id)"
              class="px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-all flex items-center gap-2"
              :class="isExamboardSelected(index, examboard.id)
                ? 'border-brand-blue bg-blue-50 text-gray-900'
                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'"
            >
              <span
                class="material-symbols-outlined text-base"
                :class="isExamboardSelected(index, examboard.id) ? 'text-brand-blue' : 'text-gray-300'"
              >
                {{ isExamboardSelected(index, examboard.id) ? 'check_box' : 'check_box_outline_blank' }}
              </span>
              {{ examboard.name }}
            </button>
          </div>
          <p v-if="displayErrors[`teaching.${index}.examboard_ids`]" class="text-red-500 text-sm mt-2">
            {{ displayErrors[`teaching.${index}.examboard_ids`] }}
          </p>
        </div>
      </div>
    </div>

    <button
      type="button"
      @click="addRow"
      class="w-full mt-4 bg-blue-50 border-2 border-dashed border-brand-blue rounded-lg p-4 text-center hover:bg-blue-100 transition-all flex items-center justify-center"
    >
      <span class="material-symbols-outlined text-brand-blue mr-2">add_circle</span>
      <span class="text-brand-blue font-semibold">Add {{ rows.length ? 'Another ' : '' }}Subject</span>
    </button>

    <button
      type="button"
      @click="showAddSubjectModal = true"
      class="w-full mt-3 rounded-lg p-3 text-center text-sm text-gray-600 hover:text-brand-blue transition-colors"
    >
      Can't find a subject in the list? <span class="font-semibold underline">Add it</span>
    </button>

    <!-- Add Subject Modal -->
    <AddSubjectModal
      :show="showAddSubjectModal"
      @close="showAddSubjectModal = false"
      @subject-added="handleSubjectAdded"
    />
  </div>
</template>
